/* eslint-disable */
import Duck from 'extensible-duck';
import { deliveryIDPrefixes, genDeliveryID, isBulkUploadDeliveryID } from 'constants/anytime-booking-constants';
import _cloneDeep from 'lodash/cloneDeep';
import _xor from 'lodash/xor';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import _find from 'lodash/find';
import { getOfferID, getOfferType, isAutoApplyOfferType } from 'helper/anytime-booking';
import { getInsuranceOptionID } from 'helper/insurance';

const defaultBulkRouteOptimizationState = {
  enabled: true,
  preventAutoApplyRO: true,
}
const defaultUIRouteOptimizationState = {
  enabled: false,
  preventAutoApplyRO: false,
}

const getDefaultROState = deliveryID => {
  return isBulkUploadDeliveryID(deliveryID) ? defaultBulkRouteOptimizationState : defaultUIRouteOptimizationState;
};

const makeNewDeliveryDetail = (deliveryID, bookingConfiguration, isRebook = false) => {
  const { parcelSizes } = bookingConfiguration;

  return {
    id: deliveryID,
    // use to force refresh some components
    version: 0,
    isRebook,
    fetchingQuote: false,
    fetchingPromo: false,
    routeOptimization: getDefaultROState(deliveryID),
    config: {
      parcelSizes,
    },
    detail: {
      pickUp: {},
      dropOffs: [{}],
      configuration: {
        serviceType: "",
        specialServiceType: "",
        vehicleType: "",
        additionalServices: [],
        scheduledPickupSlot: undefined,
        offerConfig: undefined,
      },
      itemWeight: undefined,
      itemType: undefined,
      parcelTotalSize: undefined,
      deliveryInstruction: undefined,
      referenceCode: undefined,
      promoCode: undefined,
      paymentTypeID: undefined,
      offerList: [],
      autoApplyOffer: {
        // UI: enable auto apply, BULK: not applied yet
        preventAutoApplyOffer: !!isBulkUploadDeliveryID(deliveryID),
      }
    },
  }
};

const updateOldDeliveryServiceConfig = (oldDelivery, updatedServiceConfig) => {
  if (!oldDelivery) {
    return oldDelivery;
  }
  const {
    serviceType,
    specialServiceType,
    vehicleType,
    scheduledPickupSlot,
    additionalServices,
  } = updatedServiceConfig;
  const configuration = {
    ...oldDelivery.detail.configuration,
  };
  if (serviceType) configuration.serviceType = serviceType;
  if (specialServiceType) configuration.specialServiceType = specialServiceType;
  if (vehicleType) configuration.vehicleType = vehicleType;

  if (scheduledPickupSlot !== undefined)
    configuration.scheduledPickupSlot = scheduledPickupSlot;

  if (additionalServices !== undefined) {
    configuration.additionalServices = additionalServices;
  }

  return {
    ...oldDelivery,
    detail: {
      ...oldDelivery.detail,
      configuration,
    },
  };
};

const updateInsuranceOptionToDropOffs = (insuranceOption, targetDropOff) => origDropOff => {
  // targetDropOff is undefined -> apply to all dropoffs
  // targetDropOff is defined -> apply to this dropoff
  if (targetDropOff && targetDropOff !== origDropOff) {
    return origDropOff;
  }

  return {
    ...origDropOff,
    insuranceOption,
  }
}

export default new Duck({
  namespace: 'GEWEB',
  store: 'NEWBOOKING',
  types: [
    'OPEN_DETAIL_OVERLAY',
    'CLOSE_DETAIL_OVERLAY',
    'OPEN_BULK_EDIT_OVERLAY',
    'CLOSE_BULK_EDIT_OVERLAY',
    'CREATE_NEW_DELIVERY',
    'ADD_DELIVERY_DETAILS',
    'ADD_DELIVERY_IDS',
    'REMOVE_DELIVERY_DETAILS',
    'REMOVE_DELIVERY_IDS',
    'BULK_CREATE_NEW_DELIVERIES',
    'DELETE_DELIVERY',
    'COPY_DELIVERY',
    'SWAP_DELIVERY_PICKUP_DROPOFF',
    'SET_PICKUP',
    'SET_DROPOFFS',
    'UPDATE_DELIVERY_STEP',
    'UPDATE_DELIVERY_GUARANTEE',
    'BULK_UPDATE_SERVICE_CONFIG',
    'UPDATE_SERVICE_CONFIG',
    'UPDATE_DELIVERY_DETAIL',
    'BULK_UPDATE_DELIVERY_DETAIL',
    'ADD_ADDITIONAL_SERVICE',
    'REMOVE_ADDITIONAL_SERVICE',
    'REMOVE_SCHEDULED_PICKUP_SLOT',
    'SET_ROUND_TRIP',
    'ENABLE_ROUTE_OPTIMIZATION',
    'APPLY_ROUTE_OPTIMIZATION',
    'REVERT_ROUTE_OPTIMIZATION',
    'FETCH_DELIVERY_QUOTE',
    'BATCH_FETCH_DELIVERY_QUOTES',
    'UPDATE_DELIVERY_QUOTE_AND_CONFIG',
    'BATCH_UPDATE_DELIVERY_QUOTES_AND_CONFIGS',
    'CREATE_DELIVERIES',
    'FINISH_CREATING_DELIVERIES',
    'RESET_CREATING_DELIVERIES_RESULT',
    'REMOVE_SUCCESSFUL_DELIVERIES',
    'REBOOK',
    'REBOOK_WITH_DETAIL',
    'REBOOK_FAILED',
    'REBOOK_WITH_CODES',
    'REBOOK_WITH_DETAILS',
    'REMOVE_DROPPOFF',
    'REFRESH_DELIVERY_ID_VERSION',
    'FETCH_LAST_PICKUP_STEP',
    'FETCH_OFFER_LIST',
    'UPDATE_PROMO_LIST',
    'SET_OFFER_CONFIG',
    'INPUT_PROMO_CODE',
    'SELECT_OFFER_FROM_LIST',
    'UPDATE_PREFILL_SENDER_INFO',
    'UPDATE_DROP_OFF_INFO',
    'SET_BOOKING_CONFIGURATION',
  ],
  creators: duck => ({
    openDetailOverlay: (deliveryID, overlayType) => ({ type: duck.types.OPEN_DETAIL_OVERLAY, deliveryID, overlayType }),
    closeDetailOverlay: () => ({ type: duck.types.CLOSE_DETAIL_OVERLAY }),
    openBulkEditOverlay: (deliveryIDs, overlayType) => ({ type: duck.types.OPEN_BULK_EDIT_OVERLAY, deliveryIDs, overlayType }),
    closeBulkEditOverlay: () => ({ type: duck.types.CLOSE_BULK_EDIT_OVERLAY }),
    createNewDelivery: (bookingConfiguration = {}) => ({ type: duck.types.CREATE_NEW_DELIVERY, bookingConfiguration }),
    addDeliveryDetails: (deliveryDetails) => ({ type: duck.types.ADD_DELIVERY_DETAILS, deliveryDetails }),
    addDeliveryIDs: (deliveryIDs) => ({ type: duck.types.ADD_DELIVERY_IDS, deliveryIDs }),
    removeDeliveryDetails: (deliveryIDs) => ({ type: duck.types.REMOVE_DELIVERY_DETAILS, deliveryIDs }),
    removeDeliveryIDs: (deliveryIDs) => ({ type: duck.types.REMOVE_DELIVERY_IDS, deliveryIDs }),
    bulkCreateNewDeliveries: (deliveryData, bookingConfiguration = {}) => ({ type: duck.types.BULK_CREATE_NEW_DELIVERIES, deliveryData, bookingConfiguration }),
    deleteDelivery: deliveryID => ({ type: duck.types.DELETE_DELIVERY, deliveryID }),
    copyDelivery: (deliveryID, newDeliveryID, dropOffs) => ({ type: duck.types.COPY_DELIVERY, deliveryID, newDeliveryID, dropOffs }),
    swapDeliveryPickUpDropOff: deliveryID => ({ type: duck.types.SWAP_DELIVERY_PICKUP_DROPOFF, deliveryID }),
    setPickUp: (deliveryID, pickUp, source = 'MODIFIED') => ({ type: duck.types.SET_PICKUP, deliveryID, pickUp, source }),
    setDropOffs: (deliveryID, dropOffs, updateVersion = false) => ({ type: duck.types.SET_DROPOFFS, deliveryID, dropOffs, updateVersion }),
    removeDroppOff: (deliveryID, index) => ({ type: duck.types.REMOVE_DROPPOFF, deliveryID, index }),
    updateDeliveryStep: (deliveryID, isPickUp, index, fieldName, fieldValue) => ({ type: duck.types.UPDATE_DELIVERY_STEP, deliveryID, isPickUp, index, fieldName, fieldValue }),
    updateDeliveryGuarantee: (deliveryIDs, dropOff, insuranceOptionID) => ({ type: duck.types.UPDATE_DELIVERY_GUARANTEE, deliveryIDs, dropOff, insuranceOptionID }),
    bulkUpdateServiceConfig: (deliveryIDs, updatedServiceConfig) => ({ type: duck.types.BULK_UPDATE_SERVICE_CONFIG, deliveryIDs, updatedServiceConfig }),
    updateServiceConfig: (deliveryID, updatedServiceConfig) => ({ type: duck.types.UPDATE_SERVICE_CONFIG, deliveryID, updatedServiceConfig }),
    addAdditionalService: (deliveryID, additionalService) => ({ type: duck.types.ADD_ADDITIONAL_SERVICE, deliveryID, additionalService }),
    removeAdditionalService: (deliveryID, additionalServiceID) => ({ type: duck.types.REMOVE_ADDITIONAL_SERVICE, deliveryID, additionalServiceID }),
    updateDeliveryDetail: (deliveryID, fieldName, fieldValue) => ({ type: duck.types.UPDATE_DELIVERY_DETAIL, deliveryID, fieldName, fieldValue }),
    bulkUpdateDeliveryDetail: (deliveryIDs, updatedDetail) => ({ type: duck.types.BULK_UPDATE_DELIVERY_DETAIL, deliveryIDs, updatedDetail }),
    removeScheduledPickupSlot: deliveryID => ({ type: duck.types.REMOVE_SCHEDULED_PICKUP_SLOT, deliveryID }),
    setRoundTrip: (deliveryID, isRoundTrip, autoRoundTrip = false) => ({ type: duck.types.SET_ROUND_TRIP, deliveryID, isRoundTrip, autoRoundTrip }),
    enableRouteOptimization: deliveryID => ({ type: duck.types.ENABLE_ROUTE_OPTIMIZATION, deliveryID }),
    applyRouteOptimization: (deliveryID, routeOptimizationResult) => ({ type: duck.types.APPLY_ROUTE_OPTIMIZATION, deliveryID, routeOptimizationResult }),
    revertRouteOptimization: deliveryID => ({ type: duck.types.REVERT_ROUTE_OPTIMIZATION, deliveryID }),
    fetchDeliveryQuote: (deliveryID, callback) => ({ type: duck.types.FETCH_DELIVERY_QUOTE, deliveryID, callback }),
    batchFetchDeliveryQuotes: deliveryIDs => ({ type: duck.types.BATCH_FETCH_DELIVERY_QUOTES, deliveryIDs }),
    updateDeliveryQuoteAndConfig: (deliveryID, payload) => ({ type: duck.types.UPDATE_DELIVERY_QUOTE_AND_CONFIG, deliveryID, payload }),
    batchUpdateDeliveryQuotesAndConfigs: updatedDeliveries => ({ type: duck.types.BATCH_UPDATE_DELIVERY_QUOTES_AND_CONFIGS, updatedDeliveries }),
    createDeliveries: ({ deliveryIDs, paymentTransactionID, bookingMode, skipDuplicateBookingCheckDeliveryIDs }) => ({ type: duck.types.CREATE_DELIVERIES, deliveryIDs, paymentTransactionID, bookingMode, skipDuplicateBookingCheckDeliveryIDs }),
    finishCreatingDeliveries: (successfulDeliveryIDs, failedDeliveryIDs, error, deliveryErrors, bookingMode) => ({ type: duck.types.FINISH_CREATING_DELIVERIES, successfulDeliveryIDs, failedDeliveryIDs, error, deliveryErrors, bookingMode }),
    resetCreatingDeliveriesResult: () => ({ type: duck.types.RESET_CREATING_DELIVERIES_RESULT }),
    removeSuccessfulDeliveries: successfulDeliveryIDs => ({ type: duck.types.REMOVE_SUCCESSFUL_DELIVERIES, successfulDeliveryIDs }),
    rebook: (webbooking, callback) => ({ type: duck.types.REBOOK, webbooking, callback }),
    rebookWithCodes: (bookings, rebookJobID) => ({ type: duck.types.REBOOK_WITH_CODES, bookings, rebookJobID }),
    rebookWithDetail: (detail, bookingConfiguration = {}) => ({ type: duck.types.REBOOK_WITH_DETAIL, detail, bookingConfiguration }),
    rebookFailed: () => ({ type: duck.types.REBOOK_FAILED }),
    rebookWithDetails: (details, rebookJobID, bookingConfiguration = {}) => ({ type: duck.types.REBOOK_WITH_DETAILS, details, rebookJobID, bookingConfiguration }),
    refreshDeliveryIDVersion: deliveryID => ({ type: duck.types.REFRESH_DELIVERY_ID_VERSION, deliveryID }),
    fetchOfferList: deliveryID => ({ type: duck.types.FETCH_OFFER_LIST, deliveryID }),
    updateOfferList: (deliveryID, offers) => ({ type: duck.types.UPDATE_PROMO_LIST, deliveryID, offers }),
    setOfferConfig: (deliveryID, offer) => ({ type: duck.types.SET_OFFER_CONFIG, deliveryID, offer }),
    inputPromoCode: (deliveryID, promoCode) => ({ type: duck.types.INPUT_PROMO_CODE, deliveryID, promoCode }),
    updatePrefillSenderInfo: (senderInfo) => ({ type: duck.types.UPDATE_PREFILL_SENDER_INFO, senderInfo }),
    updateDropOffInfo: (deliveryID, index, newPOI) => ({ type: duck.types.UPDATE_DROP_OFF_INFO, deliveryID, index, newPOI }),
    setBookingConfiguration: (bookingConfiguration) => ({ type: duck.types.SET_BOOKING_CONFIGURATION, bookingConfiguration }),
  }),
  initialState: {
    creatingDeliveries: false,
    creatingDeliveriesResult: undefined,
    detailOverlay: { visible: false },
    bulkEditOverlay: { visible: false },
    deliveryIDs: [],
    deliveryDetails: {},
    prefillSenderInfo: undefined,
    rebookResults: {},
    fetchingQuotes: false,
  },
  reducer: (state, action, duck) => {
    switch (action.type) {
      case duck.types.CREATE_DELIVERIES: {
        return {
          ...state,
          creatingDeliveries: true,
        }
      }
      case duck.types.FINISH_CREATING_DELIVERIES: {
        const { successfulDeliveryIDs = [], failedDeliveryIDs = [], error, deliveryErrors } = action;
        return {
          ...state,
          creatingDeliveries: false,
          creatingDeliveriesResult: {
            successfulDeliveryIDs,
            failedDeliveryIDs,
            error,
            deliveryErrors,
          }
        }
      }
      case duck.types.REMOVE_SUCCESSFUL_DELIVERIES: {
        const { successfulDeliveryIDs } = action;
        return {
          ...state,
          deliveryIDs: _xor(state.deliveryIDs, successfulDeliveryIDs)
        }
      }
      case duck.types.RESET_CREATING_DELIVERIES_RESULT: {
        return {
          ...state,
          creatingDeliveriesResult: undefined,
        }
      }
      case duck.types.OPEN_DETAIL_OVERLAY: {
        const { deliveryID, overlayType } = action;
        return {
          ...state,
          detailOverlay: {
            visible: true,
            deliveryID,
            overlayType,
          }
        }
      }
      case duck.types.CLOSE_DETAIL_OVERLAY: {
        // when closing the overlay, clear ro data
        const { deliveryID } = state.detailOverlay;
        const oldDelivery = state.deliveryDetails[deliveryID];
        return {
          ...state,
          detailOverlay: {
            ...state.detailOverlay,
            visible: false,
          },
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              routeOptimization: {
                enabled: false,
              },
            }
          }
        }
      }
      case duck.types.OPEN_BULK_EDIT_OVERLAY: {
        const { deliveryIDs, overlayType } = action;
        return {
          ...state,
          bulkEditOverlay: {
            visible: true,
            deliveryIDs,
            overlayType,
          }
        }
      }
      case duck.types.CLOSE_BULK_EDIT_OVERLAY: {
        return {
          ...state,
          bulkEditOverlay: {
            ...state.bulkEditOverlay,
            visible: false,
          },
        }
      }
      case duck.types.CREATE_NEW_DELIVERY: {
        const { bookingConfiguration } = action;
        const newDeliveryID = genDeliveryID();
        const newDeliveryDetail = makeNewDeliveryDetail(newDeliveryID, bookingConfiguration);

        return {
          ...state,
          deliveryIDs: [
            ...state.deliveryIDs,
            newDeliveryID,
          ],
          deliveryDetails: {
            ...state.deliveryDetails,
            [newDeliveryID]: newDeliveryDetail,
          },
        };
      }
      case duck.types.ADD_DELIVERY_IDS: {
        const { deliveryIDs } = action;
        return {
          ...state,
          deliveryIDs: [
            ...state.deliveryIDs,
            ...deliveryIDs,
          ],
        }
      }
      case duck.types.ADD_DELIVERY_DETAILS: {
        const { deliveryDetails } = action;
        if (!deliveryDetails) {
          return state;
        }
        const newDeliveryDetails = {};
        deliveryDetails.forEach(deliveryDetail => {
          newDeliveryDetails[_get(deliveryDetail, 'id')] = deliveryDetail;
        });
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            ...newDeliveryDetails,
          },
        };
      }
      case duck.types.REMOVE_DELIVERY_IDS: {
        const { deliveryIDs } = action;
        return {
          ...state,
          deliveryIDs: _xor(state.deliveryIDs, deliveryIDs)
        }
      }
      case duck.types.REMOVE_DELIVERY_DETAILS: {
        const { deliveryIDs } = action;
        let newDeliveryDetails = state.deliveryDetails;
        deliveryIDs.forEach(deliveryID => {
          delete newDeliveryDetails[deliveryID];
        });
        return {
          ...state,
          deliveryDetails: { ...newDeliveryDetails },
        };
      }
      case duck.types.BULK_CREATE_NEW_DELIVERIES: {
        const { deliveryData, bookingConfiguration } = action;
        if (!deliveryData) {
          return state
        }

        const newDeliveryDetails = {};
        deliveryData.forEach(delivery => {
          if (!delivery) return;

          const {
            deliveryID,
            pickUp = {},
            dropOffs = [{}],
            itemWeight,
            itemType,
            serviceType,
            specialServiceType = 'None',
            vehicleType,
            additionalServices = [],
            isNotPresetSequence = false,
            referenceCode,
            parcelTotalSize,
          } = delivery;
          const isMSD = _get(dropOffs, 'length', 1) > 1;
          const newDelivery = makeNewDeliveryDetail(deliveryID, bookingConfiguration);
          newDelivery.detail.pickUp = pickUp;
          newDelivery.detail.dropOffs = dropOffs;
          newDelivery.detail.itemWeight = itemWeight;
          newDelivery.detail.itemType = itemType;
          newDelivery.detail.parcelTotalSize = parcelTotalSize;
          newDelivery.detail.referenceCode = referenceCode;
          newDelivery.detail.configuration.serviceType = isMSD ? 'INSTANT' : serviceType;
          newDelivery.detail.configuration.specialServiceType = specialServiceType;
          newDelivery.detail.configuration.vehicleType = vehicleType;
          newDelivery.detail.configuration.additionalServices = additionalServices;
          newDelivery.routeOptimization = {
            enabled: true,
            preventAutoApplyRO: !isNotPresetSequence
          }

          newDeliveryDetails[deliveryID] = newDelivery;
        })
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            ...newDeliveryDetails,
          },
        };
      }
      case duck.types.DELETE_DELIVERY: {
        const { deliveryID } = action;
        return {
          ...state,
          deliveryIDs: state.deliveryIDs.filter(dID => dID !== deliveryID),
        };
      }
      case duck.types.COPY_DELIVERY: {
        const { deliveryID, dropOffs, newDeliveryID } = action;
        const isBulkUpload = isBulkUploadDeliveryID(deliveryID);
        const newID = newDeliveryID ? newDeliveryID : genDeliveryID(isBulkUpload ?
          deliveryIDPrefixes.BulkUpload : deliveryIDPrefixes.Default);
        const newDelivery = {
          ..._cloneDeep(state.deliveryDetails[deliveryID]),
          id: newID,
        };

        _set(newDelivery, 'detail.configuration.offerConfig', undefined);
        _set(newDelivery, 'detail.configuration.selectedOffer', undefined);
        _set(newDelivery, 'detail.configuration.offerList', []);

        if (dropOffs) {
          _set(newDelivery, 'detail.dropOffs', dropOffs);
        }

        if (isBulkUpload) {
          return {
            ...state,
            deliveryDetails: {
              ...state.deliveryDetails,
              [newID]: newDelivery,
            },
          };
        }

        return {
          ...state,
          deliveryIDs: [
            ...state.deliveryIDs,
            newID,
          ],
          deliveryDetails: {
            ...state.deliveryDetails,
            [newID]: newDelivery,
          },
        };
      }
      case duck.types.SWAP_DELIVERY_PICKUP_DROPOFF: {
        const deliveryID = action.deliveryID;
        const oldDelivery = state.deliveryDetails[deliveryID];
        const oldPickUpStep = _get(oldDelivery, 'detail.pickUp', {})
        const oldDropOffStep = _get(oldDelivery, 'detail.dropOffs[0]', {})
        if (!oldDelivery || !oldPickUpStep || !oldDropOffStep) {
          return state;
        }
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              detail: {
                ...oldDelivery.detail,
                pickUp: oldDropOffStep,
                dropOffs: [oldPickUpStep],
              }
            }
          }
        }
      }
      case duck.types.SET_PICKUP: {
        const { deliveryID, pickUp, source } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        if (!oldDelivery) {
          return state;
        }
        const oldPickup = oldDelivery.detail.pickUp;
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              detail: {
                ...oldDelivery.detail,
                pickUp: {
                  ...oldPickup,
                  ...pickUp,
                  source,
                },
              }
            }
          }
        }
      }
      case duck.types.SET_DROPOFFS: {
        const { deliveryID, dropOffs, updateVersion = false } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        if (!oldDelivery) {
          return state;
        }
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              version: updateVersion ? oldDelivery.version + 1 : oldDelivery.version,
              routeOptimization: getDefaultROState(deliveryID),
              detail: {
                ...oldDelivery.detail,
                dropOffs: dropOffs,
              }
            }
          }
        }
      }
      case duck.types.REMOVE_DROPPOFF: {
        const deliveryID = action.deliveryID;
        const oldDelivery = state.deliveryDetails[deliveryID];
        if (!oldDelivery) {
          return state;
        }
        const newDropOffs = _cloneDeep(_get(oldDelivery, ['detail', 'dropOffs'], []));
        newDropOffs.splice(action.index, 1);
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              version: oldDelivery.version + 1,
              routeOptimization: getDefaultROState(deliveryID),
              detail: {
                ...oldDelivery.detail,
                dropOffs: newDropOffs,
              }
            }
          }
        }
      }
      case duck.types.UPDATE_DELIVERY_STEP: {
        const { deliveryID, isPickUp, index, fieldName, fieldValue } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        if (!oldDelivery) {
          return state;
        }
        const oldStep = isPickUp
          ? _get(oldDelivery, ['detail', 'pickUp'], {})
          : _get(oldDelivery, ['detail', 'dropOffs', index], {});
        const newStep = {
          ...oldStep,
          [fieldName]: fieldValue,
        };
        const newDelivery = {
          ...oldDelivery,
          detail: {
            ...oldDelivery.detail,
          }
        };
        if (isPickUp) {
          _set(newDelivery, ['detail', 'pickUp'], newStep);
        } else {
          _set(newDelivery, ['detail', 'dropOffs', index], newStep);
        }
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: newDelivery,
          }
        }
      }
      case duck.types.UPDATE_DELIVERY_GUARANTEE: {
        const { deliveryIDs, dropOff, insuranceOptionID } = action;
        const newDeliveryDetails = {};

        deliveryIDs.forEach(deliveryID => {
          const oldDelivery = state.deliveryDetails[deliveryID];
          const insuranceOption = insuranceOptionID !== undefined ? _find(_get(oldDelivery, 'quote.insurance.options'), option => getInsuranceOptionID(option) === insuranceOptionID) : undefined;
          newDeliveryDetails[deliveryID] = {
            ...oldDelivery,
            detail: {
              ...oldDelivery.detail,
              dropOffs: oldDelivery.detail.dropOffs.map(updateInsuranceOptionToDropOffs(insuranceOption, dropOff))
            },
          }
        })

        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            ...newDeliveryDetails,
          }
        }
      }
      case duck.types.BULK_UPDATE_SERVICE_CONFIG: {
        const { deliveryIDs, updatedServiceConfig } = action;
        const newDeliveryDetails = {};
        deliveryIDs.forEach(deliveryID => {
          const oldDelivery = state.deliveryDetails[deliveryID];
          if (oldDelivery) {
            newDeliveryDetails[deliveryID] = updateOldDeliveryServiceConfig(oldDelivery, updatedServiceConfig)
          }
        });
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            ...newDeliveryDetails,
          }
        }
      }
      case duck.types.UPDATE_SERVICE_CONFIG: {
        const deliveryID = action.deliveryID;
        const oldDelivery = state.deliveryDetails[deliveryID];
        if (!oldDelivery) {
          return state;
        }
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: updateOldDeliveryServiceConfig(oldDelivery, action.updatedServiceConfig),
          }
        }
      }
      case duck.types.BULK_UPDATE_DELIVERY_DETAIL: {
        const { deliveryIDs, updatedDetail } = action;
        const newDeliveryDetails = {};
        deliveryIDs.forEach(deliveryID => {
          const oldDelivery = state.deliveryDetails[deliveryID];
          if (oldDelivery) {
            newDeliveryDetails[deliveryID] = {
              ...oldDelivery,
              detail: {
                ...oldDelivery.detail,
                ...updatedDetail,
              }
            }
          }
        });
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            ...newDeliveryDetails,
          }
        }
      }
      case duck.types.UPDATE_DELIVERY_DETAIL: {
        const { deliveryID, fieldName, fieldValue } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        if (!oldDelivery) {
          return state;
        }
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              detail: {
                ...oldDelivery.detail,
                [fieldName]: fieldValue,
              }
            }
          }
        }
      }
      case duck.types.ADD_ADDITIONAL_SERVICE: {
        const { deliveryID, additionalService } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        if (!oldDelivery) {
          return state;
        }
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              detail: {
                ...oldDelivery.detail,
                configuration: {
                  ...oldDelivery.detail.configuration,
                  additionalServices: [
                    ...oldDelivery.detail.configuration.additionalServices.filter(ast => ast.id !== additionalService.id),
                    additionalService,
                  ]
                },
              }
            }
          }
        }
      }
      case duck.types.REMOVE_ADDITIONAL_SERVICE: {
        const { deliveryID, additionalServiceID } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        if (!oldDelivery) {
          return state;
        }
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              detail: {
                ...oldDelivery.detail,
                configuration: {
                  ...oldDelivery.detail.configuration,
                  additionalServices: oldDelivery.detail.configuration.additionalServices.filter(ast => ast.id !== additionalServiceID),
                },
              }
            }
          }
        }
      }
      case duck.types.REMOVE_SCHEDULED_PICKUP_SLOT: {
        const deliveryID = action.deliveryID;
        const oldDelivery = state.deliveryDetails[deliveryID];
        if (!oldDelivery) {
          return state;
        }
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              detail: {
                ...oldDelivery.detail,
                configuration: {
                  ...oldDelivery.detail.configuration,
                  scheduledPickupSlot: undefined,
                },
              }
            }
          }
        }
      }
      case duck.types.SET_ROUND_TRIP: {
        const { deliveryID, isRoundTrip, autoRoundTrip } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              detail: {
                ...oldDelivery.detail,
                isRoundTrip,
                autoRoundTrip,
              },
              routeOptimization: {
                enabled: false,
              },
            }
          }
        }
      }
      case duck.types.ENABLE_ROUTE_OPTIMIZATION: {
        const { deliveryID } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              routeOptimization: {
                enabled: true,
                preventAutoApplyRO: false
              },
            }
          }
        }
      }
      case duck.types.APPLY_ROUTE_OPTIMIZATION: {
        const { deliveryID, routeOptimizationResult } = action;
        const { OptimizedSteps = [] } = routeOptimizationResult;
        if (_isEmpty(OptimizedSteps)) {
          // nothing to apply
          return state;
        }
        const optimizedSteps = OptimizedSteps.slice(1); // remove pickup
        const oldDelivery = state.deliveryDetails[deliveryID];
        const oldDetail = oldDelivery.detail;
        const isRoundTrip = oldDetail.isRoundTrip;
        if (isRoundTrip) {
          // round trip set by flag, `dropOffs` won't contain `pickup`
          optimizedSteps.pop(); // pop the last stop which will be 0
        }
        if (optimizedSteps.length !== oldDetail.dropOffs.length) {
          // do not apply error state
          return state;
        }
        const optimizedDropOffIndexes = optimizedSteps
          .map((i, _, steps) => {
            if (i === 0) {
              // for round trip set manually, 0 -> last dropOff
              return steps.length - 1;
            }
            return i - 1;
          });
        const newDropOffs = optimizedDropOffIndexes.map(i => oldDetail.dropOffs[i]);
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              fetchingQuote: false,
              version: oldDelivery.version + 1,
              detail: {
                ...oldDetail,
                dropOffs: newDropOffs,
              },
              routeOptimization: {
                enabled: true,
                preventAutoApplyRO: false,
                ...routeOptimizationResult,
                optimizedDropOffIndexes,
              },
            }
          }
        }
      }
      case duck.types.REVERT_ROUTE_OPTIMIZATION: {
        const { deliveryID } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        const { optimizedDropOffIndexes } = oldDelivery.routeOptimization;
        const oldDetail = oldDelivery.detail;
        const revertedDropOffs = [];
        optimizedDropOffIndexes.forEach((oi, ni) => revertedDropOffs[oi] = oldDetail.dropOffs[ni]);
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              version: oldDelivery.version + 1,
              detail: {
                ...oldDetail,
                dropOffs: revertedDropOffs,
              },
              routeOptimization: getDefaultROState(deliveryID),
            }
          }
        }
      }
      case duck.types.FETCH_DELIVERY_QUOTE: {
        const { deliveryID } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              fetchingQuote: true,
            }
          },
          fetchingQuotes: true,
        }
      }
      case duck.types.BATCH_FETCH_DELIVERY_QUOTES: {
        const { deliveryIDs } = action;
        const newDeliveryDetails = {};
        deliveryIDs.forEach(deliveryID => {
          const oldDelivery = state.deliveryDetails[deliveryID];
          if (oldDelivery) {
            newDeliveryDetails[deliveryID] = {
              ...oldDelivery,
              fetchingQuote: true,
            }
          }
        });
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            ...newDeliveryDetails,
          },
          fetchingQuotes: true,
        }
      }
      case duck.types.UPDATE_DELIVERY_QUOTE_AND_CONFIG: {
        const { deliveryID, payload } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        const quote = _get(payload, 'quote');
        const discount = _get(payload, 'quote.discount');
        const discountEligibilityError = _get(payload, 'quote.discountEligibilityError');
        const oldDetailConfiguration = _get(oldDelivery, 'detail.configuration');
        let oldOfferConfig = _get(oldDetailConfiguration, 'offerConfig');

        const { paxExpressError } = payload;
        if (!paxExpressError) {
          payload.paxExpressError = undefined;
        }

        const oldOfferType = getOfferType(oldOfferConfig);
        if (isAutoApplyOfferType(oldOfferType)) {
          // Auto apply: Remove current offer
          oldDetailConfiguration.offerConfig = null;
          oldOfferConfig = null;
        }

        if (oldOfferConfig) {
          if (discountEligibilityError) {
            oldDetailConfiguration.offerConfig = {
              ...oldOfferConfig,
              discountEligibilityError,
            };
          } else {
            oldDetailConfiguration.offerConfig = {
              ...oldOfferConfig,
              ...discount,
              discountEligibilityError: undefined,
              offerType: _get(discount, 'type'),
            };
          }
        } else {
          // Auto apply offer
          if (discount) {
            oldDetailConfiguration.offerConfig = {
              ...oldOfferConfig,
              ...discount,
              discountEligibilityError: undefined,
              offerType: _get(discount, 'type'),
            };
          }
        }

        if (!quote) {
          // set same value as no vehicle type selected
          oldDetailConfiguration.vehicleType = '';
        }

        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              fetchingQuote: false,
              ...payload,
              detail: {
                ...oldDelivery.detail,
                configuration: {
                  ...oldDetailConfiguration,
                },
              }
            }
          },
          fetchingQuotes: false,
        }
      }
      case duck.types.BATCH_UPDATE_DELIVERY_QUOTES_AND_CONFIGS: {
        const { updatedDeliveries } = action;
        if (!updatedDeliveries || Object.keys(updatedDeliveries).length <= 0) {
          return state;
        }
        const newDeliveryDetails = {};
        Object.entries(updatedDeliveries)
          .forEach(([deliveryID, payload]) => {
            const oldDelivery = state.deliveryDetails[deliveryID];
            newDeliveryDetails[deliveryID] = {
              ...oldDelivery,
              fetchingQuote: false,
              ...payload,
            };
          })
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            ...newDeliveryDetails,
          },
          fetchingQuotes: false,
        }
      }
      case duck.types.REBOOK_WITH_DETAIL: {
        const { detail, bookingConfiguration } = action;
        const newDeliveryID = genDeliveryID();
        const newDelivery = makeNewDeliveryDetail(newDeliveryID, bookingConfiguration, true)
        newDelivery.detail = detail
        return {
          ...state,
          deliveryIDs: [newDeliveryID],
          deliveryDetails: {
            [newDeliveryID]: newDelivery,
          }
        }
      }
      case duck.types.REBOOK_WITH_DETAILS: {
        const { details, rebookJobID, bookingConfiguration } = action;
        const newDeliveries = {};
        details.forEach(detail => {
          if (!detail) return;
          const newDeliveryID = genDeliveryID();
          const newDelivery = makeNewDeliveryDetail(newDeliveryID, bookingConfiguration, true)
          newDelivery.detail = detail;
          newDeliveries[newDeliveryID] = newDelivery;
        })
        const deliveryIDs = Object.keys(newDeliveries);
        return {
          ...state,
          deliveryIDs,
          deliveryDetails: newDeliveries,
          rebookResults: {
            ...state.rebookResults,
            [rebookJobID]: { successCount: deliveryIDs.length },
          }
        }
      }
      case duck.types.REFRESH_DELIVERY_ID_VERSION: {
        const { deliveryID } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        if (!oldDelivery) {
          return state;
        }
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              version: oldDelivery.version + 1,
            }
          }
        }
      }
      case duck.types.FETCH_OFFER_LIST: {
        const { deliveryID } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              fetchingPromo: true,
            }
          }
        }
      }
      case duck.types.UPDATE_PROMO_LIST: {
        const { deliveryID, offers } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              fetchingPromo: false,
              detail: {
                ...oldDelivery.detail,
                offerList: [...offers]
              }
            }
          }
        }
      }
      case duck.types.SET_OFFER_CONFIG: {
        const { deliveryID, offer } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];

        const { offerConfig, ...rest } = oldDelivery.detail.configuration;
        let { offerList } = oldDelivery.detail;
        let newConfiguration = {
          ...rest,
        };

        const currentOfferID = getOfferID(offerConfig);
        const newOfferID = getOfferID(offer);
        if (currentOfferID === newOfferID) {
          // Don't need to set
          return state;
        }

        if (offer) {
          newConfiguration = {
            ...newConfiguration,
            offerConfig: {
              ...offer,
            },
          }
        }

        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              fetchingPromo: false,
              detail: {
                ...oldDelivery.detail,
                configuration: {
                  ...newConfiguration,
                },
                autoApplyOffer: {
                  preventAutoApplyOffer: true
                },
                offerList: [...offerList]
              }
            }
          }
        }
      }
      case duck.types.UPDATE_PREFILL_SENDER_INFO: {
        const { senderInfo } = action;
        return {
          ...state,
          prefillSenderInfo: senderInfo
        }
      }
      case duck.types.UPDATE_DROP_OFF_INFO: {
        const { deliveryID, index, newPOI } = action;
        const oldDelivery = state.deliveryDetails[deliveryID];
        if (!oldDelivery) {
          return state;
        }
        const oldDropOffs = _get(oldDelivery, ['detail', 'dropOffs'], []);
        const currentDropOff = { ...oldDropOffs[index] };
        currentDropOff.name = _get(newPOI, 'sp_ext_info.contact_name');
        currentDropOff.phone = _get(newPOI, 'sp_ext_info.contact_number');
        currentDropOff.locationNote = _get(newPOI, 'sp_ext_info.address_details');
        currentDropOff.poi = newPOI;
        oldDropOffs[index] = currentDropOff;
        return {
          ...state,
          deliveryDetails: {
            ...state.deliveryDetails,
            [deliveryID]: {
              ...oldDelivery,
              version: oldDelivery.version + 1,
              detail: {
                ...oldDelivery.detail,
                dropOffs: [...oldDropOffs],
              }
            }
          }
        }
      }
      case duck.types.SET_BOOKING_CONFIGURATION: {
        const { bookingConfiguration } = action;
        return {
          ...state,
          bookingConfiguration,
        }
      }
      default: {
        return state;
      }
    }
  },
})
